// services/auditAPI.js
import { getAuthHeaders } from '../utils/apiAuthHeader';

import { API_BASE_URL } from '../config';

class AuditAPI {
    constructor() {
        this.baseURL = API_BASE_URL;
        console.log(`AuditAPI initialized with baseURL: ${this.baseURL}`);
    }

    // Helper method to handle unauthorized responses
    async handleRequest(url, options = {}) {
        try {
            // Add auth headers to request
            const headers = getAuthHeaders();
            const requestOptions = {
                ...options,
                headers: {
                    ...headers,
                    'Content-Type': 'application/json',
                    ...(options.headers || {})
                }
            };

            console.log(`Making request to: ${url}`);
            const response = await fetch(url, requestOptions);

            if (!response.ok) {
                const status = response.status;
                let errorMessage;

                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || `Request failed: ${response.statusText}`;
                } catch (e) {
                    errorMessage = `Request failed: ${response.statusText}`;
                }

                const error = new Error(errorMessage);
                error.status = status;

                // Handle unauthorized errors
                if (status === 401) {
                    console.error('Unauthorized access. Redirecting to login...');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    // Don't redirect immediately to allow error handling
                }

                throw error;
            }

            return await response.json();
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/'; // Redirect to login
    }

    handleError(error) {
        console.error('Audit API Error:', error);

        // If we have a response with error details
        if (error.response && error.response.data) {
            return error.response.data.detail || 'An error occurred';
        }

        // Session expired or authentication error
        if (error.status === 401 || (error.message && error.message.includes('401'))) {
            // Handle expired sessions
            return 'Session expired. Please login again.';
        }

        if (error.status === 403 || (error.message && error.message.includes('403'))) {
            return 'You do not have permission to access this resource.';
        }

        // Network error
        if (error.message && error.message.includes('Failed to fetch')) {
            return 'Cannot connect to server. Please check your connection.';
        }

        return error.message || 'An unknown error occurred';
    }

    // Audit Log Endpoints
    async getAuditLogs(params = {}) {
        const queryParams = new URLSearchParams();
        // Convert date objects to ISO strings if they exist
        const processedParams = { ...params };

        if (params.start_date instanceof Date) {
            processedParams.start_date = params.start_date.toISOString();
        }

        if (params.end_date instanceof Date) {
            processedParams.end_date = params.end_date.toISOString();
        }

        Object.entries(processedParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        console.log(`Fetching audit logs from: ${url}`);

        try {
            const response = await this.handleRequest(url);
            console.log('Audit logs API response:', response);

            // Process the response to ensure it has the expected structure
            if (response) {
                if (Array.isArray(response)) {
                    // If the response is already an array, wrap it in an object
                    return { items: response, count: response.length };
                } else if (response.items && Array.isArray(response.items)) {
                    // Response already has the expected structure
                    return response;
                } else if (typeof response === 'object') {
                    // Try to find any array in the response to use as items
                    let items = [];
                    Object.keys(response).forEach(key => {
                        if (Array.isArray(response[key])) {
                            items = response[key];
                        }
                    });

                    return {
                        ...response,
                        items: items,
                        count: items.length
                    };
                }
            }

            // Default fallback
            return { items: [], count: 0 };
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            throw error;
        }
    }

    async getTenantAuditTrail(schoolId, params = {}) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/logs/tenant/${schoolId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url);
    }

    async getUserActivityLog(userEmail, params = {}) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/logs/user/${encodeURIComponent(userEmail)}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url);
    }

    // Security Event Endpoints
    async getSecurityEvents(params = {}) {
        const queryParams = new URLSearchParams();
        // Convert date objects to ISO strings if they exist
        const processedParams = { ...params };

        if (params.start_date instanceof Date) {
            processedParams.start_date = params.start_date.toISOString();
        }

        if (params.end_date instanceof Date) {
            processedParams.end_date = params.end_date.toISOString();
        }

        Object.entries(processedParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/security/events${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url);
    }

    async getRecentSecurityAlerts(params = {}) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/security/alerts/recent${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url);
    }

    // Analytics and Reporting Endpoints
    async getAuditStatistics(params = {}) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/statistics${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url);
    }

    async getSecurityDashboard() {
        const url = `${this.baseURL}/audit/security/dashboard`;
        return this.handleRequest(url);
    }

    // Export and Maintenance Endpoints
    async exportAuditLogs(exportData) {
        // Process dates if they are Date objects
        const processedData = { ...exportData };

        if (exportData.start_date instanceof Date) {
            processedData.start_date = exportData.start_date.toISOString();
        }

        if (exportData.end_date instanceof Date) {
            processedData.end_date = exportData.end_date.toISOString();
        }

        const url = `${this.baseURL}/audit/export`;
        return this.handleRequest(url, {
            method: 'POST',
            body: JSON.stringify(processedData)
        });
    }

    async cleanupOldLogs(params = {}) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const url = `${this.baseURL}/audit/maintenance/cleanup${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.handleRequest(url, {
            method: 'POST'
        });
    }
}

const auditAPI = new AuditAPI();
export default auditAPI;